"use client";

import {
  ReadonlyURLSearchParams,
  useRouter,
  useSearchParams,
} from "next/navigation";
import { useCallback, useEffect } from "react";
import { useAPI } from "apps/website/hooks/useAPI";
import { useDatadog } from "@auth/client-sdk-react";
import debounce from "lodash.debounce";

import { PricingPlan, type TPricingPlan } from "@./suf-store";

import { trialWeekDefaultDiscountCode } from "../constants";
import { buildPathWithSearchParams } from "../utils/buildPathWithSearchParams";

import { useSignupFlowStore } from "./useSignUpFlowStore";

export const useSignUpFlowQueryParams = () => {
  const queryParams = useSearchParams();
  const router = useRouter();
  const api = useAPI();
  const datadog = useDatadog();
  const isStoreInitialised = useSignupFlowStore((state) => state.isStoreInitialised);

  /**
   * Attempts to apply a discount code and update the store.
   * @returns true if discount was successfully applied, false otherwise
   */
  const onAttemptToApplyDiscountCode = async (discountCode: string): Promise<boolean> => {
    const {
      subscription,
      callGetPriceEstimateAndUpdateStore,
      updateSubscription,
    } = useSignupFlowStore.getState();
    const shouldRefetchSubscriptionPlans: boolean = !!subscription?.trial?.totalOriginalPrice;
    try {
      const { data: { discount } } = await api.Discounts.validateDiscountCode(discountCode);
      if (discount?.code) {
        updateSubscription({
          discount: {
            discountCode: discount.code,
            type: discount.type,
            value: discount.amount ? discount.amount.toString() : undefined,
          },
        });
      }
      if (discount?.code && shouldRefetchSubscriptionPlans) {
        await callGetPriceEstimateAndUpdateStore({ discountCode: discount.code });
      }
      return !!discount?.code;
    } catch (error) {
      datadog.logger.error("Couldn't apply discount code from query parameter", { error });
      return false;
    }
  };

  // Map test variants to their associated discount codes and trial plans
  const VARIANT_DISCOUNT_MAP: Record<string, string> = {
    seven_day_trial_test: trialWeekDefaultDiscountCode,
  };

  const VARIANT_TRIAL_PLAN_MAP: Record<string, TPricingPlan> = {
    seven_day_trial_test: PricingPlan.TRIAL_WEEK,
  };

  const onHandleVariantParam = async (variantValue: string): Promise<boolean> => {
    const { updateSubscription } = useSignupFlowStore.getState();
    const discountCode = VARIANT_DISCOUNT_MAP[variantValue];
    const trialPlan = VARIANT_TRIAL_PLAN_MAP[variantValue];

    // If variant requires discount, only apply planType if discount succeeds
    if (discountCode) {
      const success = await onAttemptToApplyDiscountCode(discountCode);
      if (!success) {
        return false;
      }
    }

    if (trialPlan) {
      const { subscription } = useSignupFlowStore.getState();
      updateSubscription({
        trial: { ...subscription.trial, planType: trialPlan },
      });
    }

    return true;
  };

  // Note that order matters here; these will fire in the order they're defined.
  const queryParamMethodMap: Record<string, (value: string) => Promise<boolean>> = {
    variant: onHandleVariantParam,
    // Should be after variant, so that if both are set, discount_code has precedence
    discount_code: onAttemptToApplyDiscountCode,
  };

  // Debounce to prevent multiple calls - Should probably implement in in-flight abortion
  // for these endpoints in the future
  const onHandleQueryParamsUpdate = useCallback(debounce(async () => {
    const toRemove: string[] = [];
    for (const [ key, handler ] of Object.entries(queryParamMethodMap)) {
      await invokeParamHandler(queryParams, key, handler, toRemove);
    }
    // Remove the query params we processed so that we don't end up processing them again
    if (toRemove.length) {
      const queries = new URLSearchParams(queryParams);
      toRemove.forEach((q) => queries.delete(q));
      const newUrl = buildPathWithSearchParams(globalThis.window.location.pathname, queries);
      router.replace(newUrl);
    }
  }, 100), [
    queryParams.toString(),
    isStoreInitialised,
  ]);

  useEffect(() => {
    const debounced = onHandleQueryParamsUpdate;
    if (isStoreInitialised && !!queryParams.toString()) {
      void onHandleQueryParamsUpdate();
    }
    return () => {
      debounced.cancel();
    };
  }, [ onHandleQueryParamsUpdate ]);

};

async function invokeParamHandler(
  queryParams: ReadonlyURLSearchParams,
  key: string,
  handler: (value: string) => Promise<boolean>,
  toRemove: string[],
): Promise<void> {
  const queryValue = queryParams.get(key);
  if (!queryValue) {
    return;
  }
  const result = await handler(queryValue);
  if (result) {
    toRemove.push(key);
  }
}
